import type {
  PropertyRegistrationForm,
  FieldStaffItemWithId,
  ContractFactoryItemWithId,
  ReceiptDocumentItem,
  PropertyClassificationCode,
  PropertyStructuralFormat,
  PropertyVehicleSize,
  PropertyCheckMethod,
} from '../types/property.types';
import type { PropertyApiResponse } from '../services/properties.service';

import { padPostalCode } from '@/utils/helpers';

/**
 * Transform PropertyApiResponse (API camelCase format) to PropertyRegistrationForm (UI snake_case format)
 * Maps backend API field names to frontend form field names
 */
export const mapApiResponseToForm = (apiData: PropertyApiResponse): PropertyRegistrationForm => {
  // Transform fieldStaffs with IDs
  const fieldStaffs: FieldStaffItemWithId[] =
    apiData.fieldStaffs?.map((staff, index) => ({
      id: staff.id || `staff_${index}`,
      name: staff.name ?? '',
      tel: staff.phoneNumber ?? '', // phoneNumber -> tel
      email: staff.mailAddress ?? '', // mailAddress -> email
      index: index,
    })) ?? [];

  // Transform contractFactories with IDs
  const contractFactories: ContractFactoryItemWithId[] =
    apiData.contractFactories?.map((factory, index) => ({
      id: factory.id || `factory_${index}`,
      factory: factory.factoryName ?? '', // factoryName -> factory
      delivery_classification: factory.deliveryCategory ?? '', // deliveryCategory -> delivery_classification
      quantity: factory.quantity ?? 0,
      index: index,
    })) ?? [];

  // Keep IDs as-is (can be UUID strings or legacy numbers)
  const customerId = apiData.customerId;
  const constructionCompanyId = apiData.constructionCompanyId;

  const receiptDocuments: ReceiptDocumentItem[] =
    apiData.receiptDocuments?.map((document) => ({
      id: document.id ?? '',
      classification: document.classification ?? 0,
      title: document.title ?? '',
      remarks: document.remarks ?? '',
    })) ?? [];
  return {
    // Basic Information
    wao_property_code: apiData.waoPropertyCode ?? '',
    property_name: apiData.name ?? '', // name -> property_name
    property_name_abbreviation: apiData.nameAbbreviation ?? '', // nameAbbreviation -> property_name_abbreviation
    postal_code_first_half: padPostalCode(apiData.postalCodeFirst, 3), // postalCodeFirst -> postal_code_first_half
    postal_code_latter_half: padPostalCode(apiData.postalCodeLast, 4), // postalCodeLast -> postal_code_latter_half
    address: apiData.address ?? '',
    start_date: apiData.startDate ?? '',
    owner_name: apiData.ownerName ?? '',
    public_private_classification_code: apiData.publicPrivateClassificationCode as
      | PropertyClassificationCode
      | undefined,
    customer_id: customerId,
    construction_company_id: constructionCompanyId,
    construction_period_from: apiData.constructionPeriodFrom ?? '',
    construction_period_to: apiData.constructionPeriodTo ?? '',
    office_address: apiData.officeAddress ?? '',
    office_tel: apiData.officeTel ?? '',
    office_fax: apiData.officeFax ?? '',
    structural_format_code: apiData.structuralFormatCode as PropertyStructuralFormat | undefined,
    structural_format_others: apiData.structuralFormatOthers ?? '',
    number_of_floors_above_ground: apiData.numberOfFloorsAboveGround ?? '',
    number_of_basement_floors: apiData.numberOfBasementFloors ?? '',
    site_manager_name: apiData.siteManagerName ?? '',
    site_manager_tel: apiData.siteManagerTel ?? '',
    ready_mixed_concrete_contract_quantity: apiData.rmcContractQuantity ?? undefined, // rmcContractQuantity -> ready_mixed_concrete_contract_quantity
    vehicle_size: apiData.vehicleSize as PropertyVehicleSize | undefined,

    // Nohara Representatives
    department_code: apiData.departmentCode ?? 0,
    sales_representative_code: apiData.salesRepresentativeCode?.toString() ?? '',
    delivery_person_code: apiData.deliveryPersonCode?.toString() ?? '',

    // Onsite Staff Section
    ready_mix_concrete_staff_name: apiData.siteManagerName ?? '',
    ready_mix_concrete_staff_tel: apiData.siteManagerTel ?? '',
    field_staffs: fieldStaffs,

    // Contract Factory Section
    contract_factories: contractFactories,

    // Information Input Section
    manager_comment: apiData.managerComment ?? '',
    achievement_confirmation_company: apiData.achievementConfirmationCompany ?? '',
    check_method: apiData.checkMethod as PropertyCheckMethod | undefined,
    nohara_direct: apiData.noharaDirect ?? '',
    p_window: apiData.pWindow ?? '',
    sellers_other_gc: apiData.sellersOtherGc ?? '',
    memo: apiData.memo ?? '',
    results_caution: apiData.resultsCaution ?? '',
    jv_price: apiData.jvPrice ?? undefined,
    general_contractor_purchase_price: apiData.generalContractorPurchasePrice ?? undefined,
    exists_purchase_order: apiData.existsPurchaseOrder ?? undefined,
    cooperative: apiData.cooperative ?? '',
    link_cement_code:
      apiData.linkCementCode !== undefined && apiData.linkCementCode !== null
        ? Number(apiData.linkCementCode)
        : undefined,
    receipt_documents: receiptDocuments,
    // Archive Status
    is_archive: apiData.isArchive ?? false,
    created_user: apiData.createdStaff
      ? `${apiData.createdStaff.lastName} ${apiData.createdStaff.firstName}`
      : (apiData.createdUser ?? ''),
    created_at: apiData.createdAt ?? '',
    updated_user: apiData.updatedStaff
      ? `${apiData.updatedStaff.lastName} ${apiData.updatedStaff.firstName}`
      : (apiData.updatedUser ?? ''),
    updated_at: apiData.updatedAt ?? '',
    customer: apiData.customerSupplier,
    construction_company: apiData.constructionCompany,
  };
};
